import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import Button from './Button';

/**
 * Linked-athletes picker for a guardian row.
 *
 * Guardians have no athlete profile of their own, so navigating to
 * /admin/athletes/{guardianId} 404s ("Profile not found"). Instead, surface the
 * athlete(s) the guardian is linked to and let the admin open the athlete.
 *
 * Props:
 *   guardian       — the guardian user row ({ id, first_name, last_name, email }),
 *                    or null/undefined to render nothing.
 *   onClose        — called to dismiss.
 *   onOpenAthlete  — called with an athlete user_id when the admin picks one.
 */
export default function GuardianLinksModal({ guardian, onClose, onOpenAthlete }) {
  const { API_URL } = useAuth();
  const [state, setState] = useState({ loading: true, athletes: [], error: '' });

  useEffect(() => {
    if (!guardian) return undefined;
    let cancelled = false;
    setState({ loading: true, athletes: [], error: '' });
    axios
      .get(`${API_URL}/admin/users/${guardian.id}/linked`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      })
      .then((res) => {
        if (!cancelled) setState({ loading: false, athletes: res.data?.linked_athletes || [], error: '' });
      })
      .catch(() => {
        if (!cancelled) setState({ loading: false, athletes: [], error: 'Failed to load linked athletes.' });
      });
    return () => { cancelled = true; };
  }, [guardian, API_URL]);

  if (!guardian) return null;
  const name = `${guardian.first_name || ''} ${guardian.last_name || ''}`.trim() || guardian.email;

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 12, width: 'min(440px, 92vw)',
          maxHeight: '80vh', overflowY: 'auto', boxShadow: '0 10px 40px rgba(0,0,0,0.25)',
        }}
      >
        <div style={{ padding: '18px 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.5, textTransform: 'uppercase', color: '#7c3aed' }}>Guardian</div>
            <div style={{ fontWeight: 600, color: '#0f172a', marginTop: 3 }}>{name}</div>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 1 }}>{guardian.email}</div>
          </div>
          <Button
            variant="ghost"
            iconOnly
            onClick={onClose}
            aria-label="Close"
          >×</Button>
        </div>
        <div style={{ padding: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.5, textTransform: 'uppercase', color: '#64748b', marginBottom: 10 }}>
            Linked athlete{state.athletes.length === 1 ? '' : 's'}
          </div>
          {state.loading && <div style={{ padding: 8, color: '#64748b', fontSize: 14 }}>Loading…</div>}
          {!state.loading && state.error && <div style={{ padding: 8, color: '#dc2626', fontSize: 14 }}>{state.error}</div>}
          {!state.loading && !state.error && state.athletes.length === 0 && (
            <div style={{ padding: 8, color: '#64748b', fontSize: 14 }}>No athletes linked to this guardian.</div>
          )}
          {!state.loading && !state.error && state.athletes.map((a) => (
            <div
              key={a.user_id}
              style={{
                border: '1px solid #e2e8f0', borderRadius: 10, padding: 14, marginBottom: 10,
                display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap',
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 600, color: '#0f172a' }}>{a.name}</div>
                <div style={{ fontSize: 12, color: '#64748b' }}>{a.email}</div>
                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 3 }}>
                  {a.relation ? <span style={{ textTransform: 'capitalize' }}>{a.relation}</span> : null}
                  {a.relation && a.subscription_status ? ' · ' : null}
                  {a.subscription_status ? `Subscription: ${a.subscription_status}` : null}
                </div>
              </div>
              <Button
                variant="primary"
                size="sm"
                onClick={() => onOpenAthlete(a.user_id)}
                style={{ whiteSpace: 'nowrap' }}
                title={`Open ${a.name}'s full profile`}
              >
                View athlete details →
              </Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
